import { createTheme } from "@mui/material/styles";

/**
 * Satino-Admin-Designsprache: „Calm Editorial Utility".
 *
 * Drei Typo-Welten greifen ineinander:
 *  • JAKARTA  — UI-Schrift (Menüs, Buttons, Formulare, Body-Text)
 *  • SERIF    — editorial Display-Schrift nur für Page-Titles (H3/H4)
 *               + optional Hero-Ziffern. Sorgt für Charakter und
 *               klare Landing-Momente, ohne aufdringlich zu sein.
 *  • PLEX     — IBM Plex Mono für IDs, Codes, Zahlen-Tabellen.
 *               WEPA-Industrie-Feel, passt zu Verbrauchsmaterial-Domäne.
 */
const SATINO = {
  magenta: "#E6007E",
  magentaDark: "#B3005F",
  magentaSoft: "#FFF0F7",
  ink: "#1F1B24",
  inkMuted: "#5B5560",
  inkFaint: "#8E8791",
  paper: "#FAFAF7",
  paperWarm: "#F2EFE9",
  surface: "#FFFFFF",
  sage: "#6A7B4A",
  border: "#E8E3E6",
  borderStrong: "#D4CED1",
} as const;

const JAKARTA =
  "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const SERIF =
  "'Instrument Serif', 'Cormorant Garamond', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const PLEX =
  "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const theme = createTheme({
  palette: {
    primary: {
      main: SATINO.magenta,
      light: "#FF4BA4",
      dark: SATINO.magentaDark,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: SATINO.sage,
      light: "#8C9E6A",
      dark: "#4F5C35",
      contrastText: "#FFFFFF",
    },
    background: {
      default: SATINO.paper,
      paper: SATINO.surface,
    },
    text: {
      primary: SATINO.ink,
      secondary: SATINO.inkMuted,
    },
    divider: SATINO.border,
    error: { main: "#C6263A" },
    warning: { main: "#D97706" },
    success: { main: "#4F7A3E" },
  },
  typography: {
    fontFamily: JAKARTA,
    // Display-Welten: größte Headings nutzen die Serif-Schrift. Der Tritt
    // zwischen Overline (Sans, groß gesperrt) + Serif-H3/H4 + Sans-Body
    // gibt jeder Seite einen editorialen Rhythmus, ohne die MUI-typische
    // Typo-Monotonie.
    h1: { fontFamily: SERIF, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.05 },
    h2: { fontFamily: SERIF, fontWeight: 400, letterSpacing: "-0.018em", lineHeight: 1.08 },
    h3: { fontFamily: SERIF, fontWeight: 400, letterSpacing: "-0.015em", lineHeight: 1.1 },
    h4: { fontFamily: SERIF, fontWeight: 400, letterSpacing: "-0.012em", lineHeight: 1.15 },
    // Sub-Headings bleiben Sans — sie funktionieren als Section-Labels
    // innerhalb von Cards. Serif wäre hier zu laut.
    h5: { fontFamily: JAKARTA, fontWeight: 600, letterSpacing: "-0.005em" },
    h6: { fontFamily: JAKARTA, fontWeight: 600, letterSpacing: "-0.005em" },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: "0.01em" },
    body1: { letterSpacing: "-0.003em" },
    body2: { letterSpacing: "-0.003em" },
    // Overline: Editorial-Taglabel, hoch gesperrt. Unterscheidet sich von
    // Body durch Gesperrtheit + Pink, nicht durch reine Größe.
    overline: {
      fontWeight: 700,
      letterSpacing: "0.18em",
      fontSize: "0.72rem",
      lineHeight: 1.4,
    },
    // Caption als Semi-Mono für IDs + Meta-Infos. TypographyProps bekommen
    // fontFamily: "monospace" durchgereicht, wenn man explizit will.
    caption: { fontFamily: PLEX, letterSpacing: "-0.01em" },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: SATINO.paper,
          // Dezentes Paper-Korn für editorial Feel. SVG-data-uri bleibt
          // unter 1 KB und rendert auf allen Screens gleich.
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='matrix' values='0 0 0 0 0.12 0 0 0 0 0.1 0 0 0 0 0.13 0 0 0 0.022 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          backgroundRepeat: "repeat",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          paddingInline: 18,
        },
        containedPrimary: {
          boxShadow: "0 6px 18px -6px rgba(230, 0, 126, 0.45)",
          "&:hover": {
            boxShadow: "0 10px 22px -6px rgba(230, 0, 126, 0.55)",
          },
        },
        outlined: {
          borderColor: SATINO.borderStrong,
          "&:hover": { borderColor: SATINO.ink },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 2px rgba(31, 27, 36, 0.04), 0 8px 24px -12px rgba(31, 27, 36, 0.08)",
          border: `1px solid ${SATINO.border}`,
          backgroundImage: "none",
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
          fontFamily: PLEX,
          fontSize: "0.72rem",
          letterSpacing: "0.02em",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: SATINO.surface,
          color: SATINO.ink,
          borderBottom: `1px solid ${SATINO.border}`,
          boxShadow: "none",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: SATINO.surface,
          borderRight: `1px solid ${SATINO.border}`,
          backgroundImage: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 8,
          "&.Mui-selected": {
            backgroundColor: SATINO.magentaSoft,
            color: SATINO.magenta,
            "& .MuiListItemIcon-root": { color: SATINO.magenta },
            "&:hover": { backgroundColor: SATINO.magentaSoft },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: SATINO.border,
        },
        head: {
          fontWeight: 700,
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: SATINO.inkMuted,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: SATINO.ink,
          fontSize: "0.75rem",
          padding: "8px 12px",
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${SATINO.border}`,
        },
      },
    },
  },
});

// Named-Export der Farb- und Typo-Konstanten, falls Komponenten sie direkt
// brauchen (z. B. PageHeader, Custom-Chips mit spezifischer Tint).
export const SATINO_TOKENS = SATINO;
export const SATINO_FONTS = { JAKARTA, SERIF, PLEX };
